<template>
    <div class="dispatch-supply">
        <div class="content-block dx-card responsive-paddings">
            <div class="supply-header">
                <h5>Supply</h5>
                <DxButton text="Clear" icon="clear" styling-mode="text" @click="clearAll" />
            </div>
            <p class="muted">
                What the selected sites need, product by product, and what you have to
                deliver it with. Amounts are the litres required <strong>today</strong>
                &mdash; the same figure delivery planning loads against. Each product needs
                a tote size: a deck truck cannot carry one without it.
            </p>

            <div v-if="!rows.length" class="muted empty">
                No sites selected yet. Go back to the Sites step and pick some.
            </div>

            <template v-else>
                <div class="totals">
                    <span class="stat"><b>{{ rows.length }}</b>
                        product<span v-if="rows.length !== 1">s</span></span>
                    <span class="stat"><b>{{ fmt(totals.required) }}</b> L required</span>
                    <span class="stat"><b>{{ totals.totesNeeded }}</b> totes needed</span>
                    <span class="stat"><b>{{ totals.totesAvailable }}</b> totes available</span>
                    <span v-if="totals.shortfall > 0" class="stat stat-bad">
                        <b>{{ fmt(totals.shortfall) }}</b> L short
                    </span>
                    <span v-else-if="totals.sized" class="stat stat-ok">supply covers the run</span>
                </div>

                <div class="supply-grid">
                    <div v-for="row in rows" :key="row.product" class="supply-card dx-card"
                        :class="statusClass(row)">
                        <div class="card-head">
                            <span class="product">{{ row.product }}</span>
                            <span class="sites muted">{{ row.siteCount }}
                                site<span v-if="row.siteCount !== 1">s</span></span>
                        </div>

                        <div class="required">
                            <span class="required-value">{{ fmt(row.required) }} L</span>
                            <span class="required-label muted">required</span>
                        </div>
                        <div v-if="forecastDays" class="predicted muted">
                            {{ fmt(row.predicted) }} L predicted{{ forecastLabel }}
                        </div>

                        <div class="inputs">
                            <div class="field">
                                <label>Tote Size (L)</label>
                                <DxNumberBox :min="0" :show-spin-buttons="true" placeholder="e.g. 1000"
                                    :value="row.toteSize" @value-changed="(e) => onToteSize(row, e)" />
                            </div>
                            <div class="field">
                                <label>Totes Available</label>
                                <DxNumberBox :min="0" :show-spin-buttons="true" placeholder="0"
                                    :value="row.totesAvailable"
                                    @value-changed="(e) => onTotesAvailable(row, e)" />
                            </div>
                        </div>

                        <div class="outcome">
                            <template v-if="!row.toteSize">
                                <span class="muted">Enter a tote size to see how many totes this needs.</span>
                            </template>
                            <template v-else>
                                <span><b>{{ row.totesNeeded }}</b> tote<span
                                        v-if="row.totesNeeded !== 1">s</span> needed</span>
                                <span class="sep">&middot;</span>
                                <span><b>{{ row.totesAvailable || 0 }}</b> available</span>
                                <span class="sep">&middot;</span>
                                <span v-if="row.shortfall > 0" class="bad">
                                    short {{ fmt(row.shortfall) }} L
                                </span>
                                <span v-else class="ok">covered</span>
                            </template>
                        </div>
                    </div>
                </div>

                <p v-if="unsized.length" class="warn">
                    No tote size yet for {{ unsized.join(', ') }}. A deck truck cannot carry
                    a product without one, so
                    {{ unsized.length === 1 ? 'that product' : 'those products' }} will come
                    back unassigned from delivery planning.
                </p>
            </template>
        </div>

        <div class="nav-row">
            <DxButton icon="chevronleft" text="Previous" styling-mode="outlined" @click="onPrevious" />
            <DxButton icon="chevronright" text="Next" type="default" styling-mode="contained"
                @click="onNext" />
        </div>
    </div>
</template>

<script>
import { DxButton } from 'devextreme-vue/button';
import DxNumberBox from 'devextreme-vue/number-box';

export default {
    name: 'DispatchSupply',
    components: {
        DxButton,
        DxNumberBox,
    },
    emits: ['previous', 'next'],
    data() {
        return {
            // product name -> { toteSize, totesAvailable }
            entries: {},
        };
    },
    created() {
        // Seed from whatever was saved for this dispatch. Keyed by product name so
        // re-selecting sites keeps the figures for products that are still in play
        // and simply ignores entries for ones that are not.
        const saved = this.$store.getters['dispatch/getCurrentDispatchSupply'] || {};
        this.entries = { ...(saved.byProduct || {}) };
    },
    computed: {
        summary() {
            return this.$store.getters['dispatch/getSupplySummary'];
        },
        // Only ever reached on a tote run — the builder drops this step entirely
        // for a tanker, where the product arrives with the hauler.
        deliveryMethod() {
            return this.$store.getters['dispatch/getDeliveryMethod'];
        },
        forecastDays() {
            return this.$store.getters['dispatch/getDispatchForecastDays'];
        },
        forecastLabel() {
            return this.forecastDays ? ` in ${this.forecastDays}d` : '';
        },
        // The per-product summary joined with the user's tote figures, plus the
        // derived numbers the card shows.
        rows() {
            return this.summary.map((row) => {
                const entry = this.entries[row.product] || {};
                const toteSize = Number(entry.toteSize) || 0;
                const totesAvailable = Number(entry.totesAvailable) || 0;
                const totesNeeded = toteSize > 0 ? Math.ceil(row.required / toteSize) : 0;
                const supplied = toteSize * totesAvailable;
                return {
                    ...row,
                    toteSize: entry.toteSize ?? null,
                    totesAvailable: entry.totesAvailable ?? null,
                    totesNeeded,
                    supplied,
                    shortfall: toteSize > 0 ? Math.max(0, row.required - supplied) : 0,
                };
            });
        },
        totals() {
            const t = {
                required: 0,
                totesNeeded: 0,
                totesAvailable: 0,
                shortfall: 0,
                sized: false,
            };
            for (const row of this.rows) {
                t.required += row.required;
                t.totesNeeded += row.totesNeeded;
                t.totesAvailable += Number(row.totesAvailable) || 0;
                t.shortfall += row.shortfall;
            }
            // "Covers the run" only means something once every product is sized.
            t.sized = this.rows.length > 0 && this.rows.every((r) => r.toteSize > 0);
            return t;
        },
        unsized() {
            return this.rows.filter((r) => !r.toteSize).map((r) => r.product);
        },
    },
    methods: {
        fmt(value) {
            return Math.round(Number(value) || 0).toLocaleString();
        },
        statusClass(row) {
            if (!row.toteSize) return 'is-unsized';
            return row.shortfall > 0 ? 'is-short' : 'is-covered';
        },
        setEntry(product, patch) {
            // Replaced rather than mutated so the computed rows re-evaluate.
            this.entries = {
                ...this.entries,
                [product]: { ...(this.entries[product] || {}), ...patch },
            };
        },
        onToteSize(row, e) {
            this.setEntry(row.product, { toteSize: e.value });
        },
        onTotesAvailable(row, e) {
            this.setEntry(row.product, { totesAvailable: e.value });
        },
        clearAll() {
            this.entries = {};
        },
        // Stores the tote figures plus a snapshot of what they were entered
        // against, so Review and delivery planning can show the numbers without
        // recomputing them, and can tell when the site selection has moved on.
        persist() {
            this.$store.dispatch('dispatch/setSupply', {
                method: this.deliveryMethod,
                byProduct: { ...this.entries },
                products: this.rows.map((r) => ({
                    product: r.product,
                    siteCount: r.siteCount,
                    required: r.required,
                    predicted: r.predicted,
                    toteSize: r.toteSize,
                    totesAvailable: r.totesAvailable,
                    totesNeeded: r.totesNeeded,
                    shortfall: r.shortfall,
                })),
                totals: { ...this.totals },
            });
        },
        onPrevious() {
            this.persist();
            this.$emit('previous');
        },
        onNext() {
            this.persist();
            this.$emit('next');
        },
    },
};
</script>

<style scoped>
.dispatch-supply {
    max-width: 1100px;
    margin: 0 auto;
}

.supply-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

h5 {
    margin: 0;
    font-family: Roboto, Verdana, Geneva, Tahoma, sans-serif;
    font-weight: 300;
}

.muted {
    color: rgba(150, 150, 150, 0.9);
    font-size: 13px;
}

.empty {
    padding: 24px 0;
}

.totals {
    display: flex;
    flex-wrap: wrap;
    gap: 18px;
    margin: 18px 0 6px 0;
    font-size: 13px;
}

.stat b {
    font-size: 15px;
}

.stat-ok {
    color: #4caf50;
}

.stat-bad b {
    color: #ff5c69;
}

.supply-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
    margin-top: 16px;
}

.supply-card {
    padding: 16px 18px;
    border-radius: 6px;
    border-left: 3px solid transparent;
}

.supply-card.is-covered {
    border-left-color: #4caf50;
}

.supply-card.is-short {
    border-left-color: #ff5c69;
}

.supply-card.is-unsized {
    border-left-color: rgba(150, 150, 150, 0.5);
}

.card-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
}

.product {
    font-size: 16px;
    font-weight: 500;
}

.sites {
    white-space: nowrap;
}

.required {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 10px;
}

.required-value {
    font-size: 20px;
    color: rgb(255, 140, 40);
}

.predicted {
    margin-top: 2px;
    font-size: 12px;
}

.inputs {
    display: flex;
    gap: 12px;
    margin-top: 14px;
}

.field {
    flex: 1 1 0;
    min-width: 0;
}

.field label {
    display: block;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(150, 150, 150, 0.9);
    margin-bottom: 4px;
}

.outcome {
    margin-top: 14px;
    padding-top: 10px;
    border-top: 1px solid rgba(150, 150, 150, 0.2);
    font-size: 12px;
}

.sep {
    margin: 0 6px;
    opacity: 0.5;
}

.ok {
    color: #4caf50;
}

.bad {
    color: #ff5c69;
}

.warn {
    margin-top: 16px;
    font-size: 13px;
    color: rgb(255, 140, 40);
}

.nav-row {
    display: flex;
    justify-content: space-between;
    margin: 20px;
}
</style>
